/**
 * metricFormat — pure formatting helpers for widget rendering. The backend owns
 * every number; these only decide how an already-computed value is presented.
 */

import type { dashboard } from '@cwos/api-client';

/**
 * WHAT EACH VISUALIZATION IS CALLED — ONE MAPPING, AND IT CANNOT BE INCOMPLETE.
 *
 * This was a `switch` with a `default: return viz` arm, and `WidgetRenderer.tsx`
 * carried a SECOND, exhaustive copy of the same four cases because the exported
 * one could not be trusted to be complete. Two spellings of one mapping, and the
 * one on the export was the weaker: a `default` arm makes every member handled
 * by definition, so adding `'area'` to `WidgetVisualization` would have compiled
 * here and shipped the raw wire token `area` into a card header.
 *
 * A `Record` keyed by the union enforces the same thing through the RETURN TYPE
 * instead — the technique `figureState.ts` uses for `FIGURE_PLACEHOLDER`. A new
 * member breaks THIS DECLARATION, at the one place a human can write the word
 * for it, rather than falling through to a fallback at runtime in front of a
 * reader. There is no `default` to delete later and no second copy to drift.
 */
const VIZ_LABEL: Record<dashboard.WidgetVisualization, string> = {
  kpi: 'KPI',
  line: 'Line',
  bar: 'Bar',
  table: 'Table',
};

export function vizLabel(viz: dashboard.WidgetVisualization): string {
  return VIZ_LABEL[viz];
}

/**
 * Numeric input step for a threshold editor. Percent targets are whole numbers;
 * ratio targets (0–1, e.g. the care-gap threshold) need a fractional step so a
 * default like 0.8 is representable.
 */
export function thresholdStep(unit: dashboard.ThresholdUnit): number {
  return unit === 'percent' ? 1 : 0.05;
}

/**
 * metricDrilldownHref — where a configurable-dashboard widget's number "opens
 * to" (owner acceptance criteria: a static KPI without a drill-down is
 * incomplete). Every curated metric maps to the existing screen that already
 * shows its backing rows, so this adds no new surface — it just makes the
 * widget navigate to the real explanation instead of being a dead end.
 */
const METRIC_DRILLDOWN: Record<string, string> = {
  active_workers: '/dashboard?view=staff',
  workers_delivering_service: '/dashboard?view=staff',
  clocked_in_workers: '/dashboard?view=staff',
  working_now: '/dashboard?view=staff',
  hours_worked: '/dashboard?view=staff',
  open_sessions: '/dashboard?view=staff',
  utilization: '/dashboard?view=staff',
  no_shows: '/dashboard?view=staff',
  shifts_scheduled: '/dashboard?view=schedule',
  shifts_completed: '/dashboard?view=schedule',
  shift_cancellations: '/dashboard?view=schedule',
  completion_rate: '/dashboard?view=schedule',
  schedule_by_worker: '/dashboard?view=schedule',
  geofence_exceptions: '/dashboard?view=monitor',
  verification_failures: '/dashboard?view=monitor',
  sessions_requiring_review: '/dashboard?view=monitor',
  field_activity: '/dashboard?view=staff',
  docs_open: '/dashboard?view=documentation',
  docs_overdue: '/dashboard?view=documentation',
  docs_aging: '/dashboard?view=documentation',
  docs_by_worker: '/dashboard?view=documentation',
  exceptions_open: '/attention',
  exceptions_critical: '/attention',
  exceptions_by_category: '/attention',
  clients_served: '/dashboard?view=clients',
  appointments: '/dashboard?view=impact',
  // Service hours have their own chart and visit records on Impact.
  hours_delivered: '/dashboard?view=impact',
  care_gaps: '/dashboard?view=careGaps',
  clients_by_program: '/dashboard?view=impact',
};

export function metricDrilldownHref(metricKey: string): string | undefined {
  return METRIC_DRILLDOWN[metricKey];
}

export function formatWidgetValue(
  value: number,
  unit: dashboard.MetricUnit,
  precision: number,
): string {
  const rounded = Number(value.toFixed(precision));
  const n = rounded.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
  // A Record keyed by the union, not a switch with a default: the default arm
  // silently absorbs a new unit, which is the construct removed from vizLabel
  // above for exactly that reason. A missing key here fails at the DECLARATION.
  const suffix: Record<dashboard.MetricUnit, string> = {
    hours: 'h',
    percent: '%',
    count: '',
  };
  return `${n}${suffix[unit]}`;
}
